import { useMutation, useQuery } from "@apollo/client";
import { AppContext } from "App";
import { useModals } from "ModalProvider";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import InputSelect from "components/base/InputSelect";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import Status, { PackageStatusChain } from "components/base/Status";
import { Alert } from "components/base/Toast";
import ImagesPreview from "components/shared/ImagesPreview";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { formatDate } from "react-day-picker/moment";
import { BsGeoAlt, BsPencil, BsTrash } from "react-icons/bs";
import { FaCheckCircle } from "react-icons/fa";
import { FaAsterisk } from "react-icons/fa6";
import { FiExternalLink } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import {
  CREATE_PACKAGE,
  DELETE_PACKAGE,
  FETCH_ALL_PACKAGE_ADDRESSES,
  FETCH_PACKAGES,
  UPDATE_PACKAGE,
} from "./graphql";

const PACKAGE_PAGE_SIZE = 30;

const isPackageRelatedToMe = (pkg, user) =>
  pkg.items?.some((item) => item.cc?.some((cc) => cc.id === user.id)) ||
  pkg.checkItems?.some((checkItem) =>
    checkItem.senders?.some((sender) => sender.id === user.id),
  ) ||
  pkg.checkItems?.some((checkItem) =>
    checkItem.receivers?.some((receiver) => receiver.id === user.id),
  );

export const buildExternalTrackingLink = (pkg) => {
  if (!pkg.trackingNumber) return null;

  if (pkg.method === "FEDEX") {
    return `https://www.fedex.com/fedextrack/?trknbr=${pkg.trackingNumber}`;
  }
  if (pkg.method === "DHL") {
    return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${pkg.trackingNumber}`;
  }
  if (pkg.method === "UPS") {
    return `https://www.ups.com/track?tracknum=${pkg.trackingNumber}`;
  }
  return null;
};

const buildDestinationSummary = (address) =>
  [
    address.company,
    address.attention,
    address.streetAddress,
    address.streetAddress2,
    address.cityPostal,
    address.countryName || address.country,
    address.phone,
    address.email,
  ]
    .filter(Boolean)
    .join(" | ");

const PackageList = ({ limit, offset, q, setOffset }) => {
  const previewModal = useModals();
  const editModal = useModals();
  const listVariables = { limit, offset, q };
  const { loading, error, data } = useQuery(FETCH_PACKAGES, {
    variables: listVariables,
  });
  const { user } = useContext(AppContext);
  const navigate = useNavigate();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const total = data.packages.total;
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const packages = data.packages.results.map((i) => ({
    ...i,
    externalTrackingLink: buildExternalTrackingLink(i),
  }));

  return (
    <>
      <div className="whitespace-nowrap flex-1 overflow-auto">
        <table>
          <thead>
            <tr className=" sticky top-0 bg-gray-200 dark:bg-gray-800 z-20">
              <th className="px-6 py-3">Package</th>
              <th className="px-6">Images</th>
              <th className="px-6">Destination</th>
              <th className="px-6">Shipment Date</th>
              <th className="px-6">Creator</th>
              <th className="px-6">Shipping Charge</th>
              <th className="px-6">Tracking NO.</th>
              <th className="px-6">Courier</th>
              <th className="px-6">Matched</th>
              <th className="px-6">Status</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((pkg, index) => (
              <tr
                key={index}
                className="border-y border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-sky-50 dark:hover:bg-gray-900"
                onClick={(e) => {
                  if (
                    e.target.tagName !== "A" &&
                    e.target.tagName !== "BUTTON"
                  ) {
                    navigate(`/tools/package/${pkg.id}`);
                  }
                }}
              >
                <td className="px-6">
                  <div className="flex items-center space-x-4">
                    <Link to={`/tools/package/${pkg.id}`}>
                      {isPackageRelatedToMe(pkg, user) ? (
                        <span className="rounded bg-yellow-200 bg-opacity-50 py-1 dark:bg-amber-200/25 dark:ring-1 dark:ring-amber-200/30">
                          Package #{pkg.id}
                        </span>
                      ) : (
                        <span>Package #{pkg.id}</span>
                      )}
                    </Link>
                    <div className="flex justify-between">
                      <div className="flex space-x-2">
                        {pkg.status !== "DELIVERED" && (
                          <Button
                            data-testid="package-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              editModal.present({
                                title: "Edit Package",
                                center: true,
                                children: (
                                  <PackageForm
                                    hide={editModal.hide}
                                    pkg={pkg}
                                    action={"update"}
                                    listVariables={listVariables}
                                  />
                                ),
                                isBeingPresented: true,
                              });
                            }}
                          >
                            <BsPencil />
                          </Button>
                        )}
                        {pkg.items.length === 0 && (
                          <Button
                            data-testid="package-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              editModal.present({
                                title: "Delete Package",
                                center: true,
                                children: (
                                  <PackageForm
                                    hide={editModal.hide}
                                    pkg={pkg}
                                    action={"delete"}
                                    listVariables={listVariables}
                                  />
                                ),
                                isBeingPresented: true,
                              });
                            }}
                            danger
                          >
                            <BsTrash />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 w-full">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {pkg.images?.length > 0 &&
                      pkg.images.map((image, index) => (
                        <img
                          key={index}
                          src={image.url + "?imageView2/2/w/300"}
                          alt={image.name}
                          className="w-8 h-8 xl:w-12 xl:h-12 cursor-pointer rounded-xl flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            previewModal.present({
                              title: image.name,
                              fullscreen: true,
                              children: (
                                <ImagesPreview
                                  images={pkg.images}
                                  initialIndex={index}
                                />
                              ),
                            });
                          }}
                        />
                      ))}
                  </div>
                </td>
                <td className="px-6">{pkg.destination.shortName}</td>
                <td className="px-6">
                  {formatDate(pkg.createdAt, "YYYY-MM-DD")}
                </td>
                <td className="px-6">
                  {pkg.creator
                    ? pkg.creator.firstName || pkg.creator.lastName
                      ? `${pkg.creator.firstName} ${pkg.creator.lastName}`
                      : pkg.creator.email.split("@")[0]
                    : ""}
                </td>
                <td className="px-6">{pkg.cost} ¥</td>
                <td
                  className="px-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (pkg.externalTrackingLink)
                      window.open(pkg.externalTrackingLink);
                  }}
                >
                  {pkg.externalTrackingLink && (
                    <div className="flex items-center space-x-1 text-sky-600 hover:text-sky-700 active:text-sky-800 font-semibold">
                      <span>{pkg.trackingNumber}</span>
                      <FiExternalLink />
                    </div>
                  )}
                </td>
                <td className="px-6">{pkg.method}</td>
                <td className="px-6">
                  <FaCheckCircle
                    className={`text-base ${pkg.isMatched ? "text-green-500" : "text-gray-300"}`}
                  />
                </td>
                <td className="px-6">
                  <Status status={pkg.status} />
                </td>
              </tr>
            ))}
            {packages.length === 0 && (
              <tr>
                <td colSpan={10} className="px-6 py-10 text-center opacity-60">
                  No packages found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-6 py-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700 text-sm">
        <div className="flex items-center space-x-3">
          <Button
            prev
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          />
          <span className="opacity-70">
            Page {currentPage} / {totalPages}
          </span>
          <Button
            next
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
          />
        </div>
        <div className="opacity-60">
          {total === 0
            ? "0 / 0"
            : `${offset + 1}-${Math.min(offset + limit, total)} / ${total}`}
        </div>
      </div>
    </>
  );
};

const PackageForm = ({ hide, pkg, action, listVariables, onCreated }) => {
  const packageId = pkg ? pkg.id : "";
  const [destinationId, setDestinationId] = useState(
    pkg ? pkg.destination.id : "",
  );
  const [destinationSearchValue, setDestinationSearchValue] = useState(
    pkg ? pkg.destination.shortName : "",
  );
  const destinationIdRef = useRef(destinationId);
  const { loading, error, data } = useQuery(FETCH_ALL_PACKAGE_ADDRESSES);
  const destinationOptions = useMemo(
    () =>
      (data?.allPackageAddresses || [])
        .slice()
        .sort((a, b) => a.shortName.localeCompare(b.shortName))
        .map((address) => ({
          ...address,
          name: address.shortName,
          searchText: buildDestinationSummary(address),
        })),
    [data],
  );

  useEffect(() => {
    destinationIdRef.current = destinationId;
  }, [destinationId]);
  const refetchPackages = [
    {
      query: FETCH_PACKAGES,
      variables: listVariables || {
        limit: PACKAGE_PAGE_SIZE,
        offset: 0,
        q: "",
      },
    },
  ];

  const [createPackage] = useMutation(CREATE_PACKAGE, {
    variables: { destinationId },
    refetchQueries: refetchPackages,
    onCompleted: (data) => {
      hide();
      onCreated?.();
      Alert(
        "success",
        "The package #" + data.createPackage.package.id + " has been created.",
      );
    },
  });

  const [updatePackage, { loading: updateLoading }] = useMutation(
    UPDATE_PACKAGE,
    {
      variables: { id: packageId, destinationId },
      refetchQueries: refetchPackages,
      onCompleted: () => {
        hide();
        Alert("success", "The package #" + packageId + " has been updated.");
      },
    },
  );

  const [deletePackage] = useMutation(DELETE_PACKAGE, {
    variables: { id: packageId },
    refetchQueries: refetchPackages,
    onCompleted: () => {
      hide();
      Alert("success", "The package #" + packageId + " has been deleted.");
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const handleSubmit = () => {
    if (!destinationId) {
      Alert("error", "Please select destination.");
      return;
    }
    if (packageId) {
      updatePackage();
    } else {
      createPackage();
    }
  };

  return (
    <div data-testid="package-form">
      {action === "delete" ? (
        <>
          <p className="opacity-80 mb-4">Delete this package?</p>
          <div className="text-sm space-y-2 card bg-gray-100">
            <div className="flex">
              <label className="w-40">Package: </label># {pkg.id}
            </div>
            <div className="flex">
              <label className="w-40">Destination: </label>
              <div className="flex flex-col">
                {pkg.destination.streetAddress && (
                  <span>{pkg.destination.streetAddress}</span>
                )}
                {pkg.destination.streetAddress2 && (
                  <span>{pkg.destination.streetAddress2}</span>
                )}
                {pkg.destination.cityPostal && (
                  <span>{pkg.destination.cityPostal}</span>
                )}
                {(pkg.destination.countryName || pkg.destination.country) && (
                  <span>
                    {pkg.destination.countryName || pkg.destination.country}
                  </span>
                )}
              </div>
            </div>
            <div className="flex">
              <label className="w-40">Courier: </label>
              {pkg.method}
            </div>
            <div className="flex">
              <label className="w-40">Tracking Number: </label>
              {pkg.trackingNumber}
            </div>
            <div className="flex">
              <label className="w-40">Status: </label>
              {pkg.status}
            </div>
            <div className="flex">
              <label className="w-40">Shipment Date: </label>
              {formatDate(pkg.createdAt, "YYYY-MM-DD")}
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm space-y-2 card bg-gray-100">
          {packageId ? (
            <div className="flex">
              <label className="w-40">Package: </label># {pkg.id}
            </div>
          ) : null}
          <div className="flex items-center">
            <label className="w-40">Destination: </label>
            <InputSelect
              className="flex-1"
              inputProps={{ "data-testid": "package-destination" }}
              value={destinationSearchValue}
              onChange={(value) => {
                setDestinationSearchValue(value);
                setDestinationId("");
              }}
              onBlur={() => {
                setTimeout(() => {
                  if (!destinationIdRef.current) {
                    setDestinationSearchValue("");
                  }
                }, 0);
              }}
              onSelect={(selectedAddress) => {
                setDestinationId(selectedAddress.id);
                setDestinationSearchValue(selectedAddress.shortName);
              }}
              options={destinationOptions}
              searchFields={[
                "shortName",
                "company",
                "attention",
                "streetAddress",
                "streetAddress2",
                "cityPostal",
                "country",
                "phone",
                "email",
                "searchText",
              ]}
              placeholder="Search and select a destination"
              inputClasses="dark:bg-gray-700 dark:bg-opacity-20"
              renderOption={(selectedAddress) => {
                const destinationSummary =
                  buildDestinationSummary(selectedAddress);

                return (
                  <div className="space-y-1 py-1">
                    <div className="font-semibold">
                      {selectedAddress.shortName}
                    </div>
                    {destinationSummary ? (
                      <div className="text-xs opacity-80 whitespace-normal break-words">
                        {destinationSummary}
                      </div>
                    ) : null}
                  </div>
                );
              }}
            />
            <FaAsterisk className="text-red-500 text-xxs ml-2" />
          </div>
        </div>
      )}

      {updateLoading ? (
        <div className="relative">
          <InlineSpinner size={21} />
        </div>
      ) : (
        <div className="flex space-x-4 mt-4">
          {action === "delete" ? (
            <Button onClick={deletePackage} danger={true}>
              Delete
            </Button>
          ) : (
            <Button onClick={handleSubmit}>{pkg ? "Update" : "Create"}</Button>
          )}

          <Button onClick={hide} className=" text-gray-500 hover:text-gray-800">
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

const PackageTracking = () => {
  const { present, hide } = useModals();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    q: "",
    offset: "0",
  });
  const q = searchParams.get("q") || "";
  const offsetParam = parseInt(searchParams.get("offset") || "0", 10);
  const offset = Number.isNaN(offsetParam) ? 0 : offsetParam;
  const params = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  const setOffset = (offset) =>
    setSearchParams({ ...params, offset: String(offset) });
  const debounceSearch = useDebouncedCallback(
    (q) => setSearchParams({ ...params, q, offset: "0" }),
    500,
  );
  const firstPageVariables = { limit: PACKAGE_PAGE_SIZE, offset: 0, q };

  return (
    <Page
      title="Package Tracking"
      className="h-screen"
      leftButtons={
        <div className="flex space-x-4">
          <Button
            data-testid="create-package-btn"
            onClick={() =>
              present({
                title: "Create Package",
                center: true,
                children: (
                  <PackageForm
                    hide={hide}
                    action={"create"}
                    listVariables={firstPageVariables}
                    onCreated={() => setOffset(0)}
                  />
                ),
                isBeingPresented: true,
              })
            }
          >
            + Create Package
          </Button>
          <Button
            data-testid="destination-address-btn"
            onClick={() => navigate("/tools/package/destination")}
            icon={<BsGeoAlt />}
          >
            Destination Address
          </Button>
        </div>
      }
    >
      <div className="p-6 flex flex-col flex-1 overflow-auto">
        <div className="mb-4 max-w-xl">
          <SearchBar
            placeholder="Search package, tracking no, destination, creator or sample"
            initialQuery={q}
            onChange={debounceSearch}
          />
        </div>
        <div
          data-testid="package-list"
          className="card p-0 flex-1 min-h-0 flex flex-col overflow-hidden"
        >
          <PackageList
            limit={PACKAGE_PAGE_SIZE}
            offset={offset}
            q={q}
            setOffset={setOffset}
          />
        </div>

        <div
          data-testid="package-status-note"
          className="mt-6 opacity-70 text-xs space-y-2"
        >
          <p>A package will go through the following statuses:</p>
          <PackageStatusChain status={"DELIVERED"} />
          <div className="space-y-1">
            <div className="flex">
              <p className="w-20">Shipped: </p>
              <p>
                Now the package is on the way to the destination. You can add,
                edit, delete the sample information.
              </p>
            </div>
            <div className="flex">
              <p className="w-20">Delivered: </p>
              <p>
                Now the package has been delivered. WIS will send you a
                notification and you cannot change anything now.
              </p>
            </div>
          </div>
          <div className="font-bold">
            This highlight{" "}
            <span className="rounded bg-yellow-200 bg-opacity-50 p-1 dark:bg-amber-200/25 dark:ring-1 dark:ring-amber-200/30">
              #
            </span>{" "}
            means the package is related to you!
          </div>
        </div>
      </div>
    </Page>
  );
};

export default PackageTracking;
